import { GTFSProcessor } from './gtfs';

export class Report {
    constructor(data) {
        this.reportId = data.reportId;
        this.reportName = data.reportName || '';
        this.reportDescription = data.reportDescription || '';
        this.reportType = data.reportType || 'operational';
        this.createdAt = data.createdAt || new Date().toISOString();
        this.updatedAt = data.updatedAt || new Date().toISOString();
        this.companyId = data.companyId || null;
        this.createdBy = data.createdBy;
        this.parameters = data.parameters || {};
        this.schedule = data.schedule || null; // For scheduled reports
        this.lastRun = data.lastRun || null;
        this.status = data.status || 'draft'; // draft, active, archived
        this.format = data.format || 'csv'; // csv, pdf, excel
        this.recipients = data.recipients || []; // For automated delivery
        
        // CSV upload specific properties
        this.fileKey = data.fileKey || null; // S3 file key
        this.fileName = data.fileName || null; // Original file name
        this.fileSize = data.fileSize || null; // File size in bytes
        this.fileType = data.fileType || 'csv'; // File type (csv, xlsx, txt)
        this.reportDate = data.reportDate || null; // The date the report is for
        this.reportDates = data.reportDates || null; // The dates the report is for
        this.uploadedAt = data.uploadedAt || data.createdAt; // When the file was uploaded
        this.uploadedBy = data.uploadedBy || data.createdBy; // Who uploaded the file
    }

    static getDefaultParameters(reportType) {
        switch (reportType) {
            case 'operational':
                return {
                    dateRange: { start: null, end: null },
                    routes: [],
                    vehicles: [],
                    operators: []
                };
            case 'financial':
                return {
                    dateRange: { start: null, end: null },
                    categories: [],
                    includeDetails: true
                };
            case 'compliance':
                return {
                    dateRange: { start: null, end: null },
                    complianceTypes: [],
                    includeDrivers: true
                };
            case 'analytics':
                return {
                    dateRange: { start: null, end: null },
                    metrics: [],
                    groupBy: 'day'
                };
            default:
                return {
                    dateRange: { start: null, end: null }
                };
        }
    }

    isScheduled() {
        return !!this.schedule;
    }

    isRecentlyRun() {
        if (!this.lastRun) return false;
        const lastRunDate = new Date(this.lastRun);
        const now = new Date();
        // Check if run in the last 24 hours
        return (now - lastRunDate) < (24 * 60 * 60 * 1000);
    }

    // Get formatted report date
    getFormattedDate() {
        if (!this.reportDate) return 'Unknown date';
        return new Date(this.reportDate).toLocaleDateString();
    }

    // Get file size in human readable format
    getFormattedSize() {
        if (!this.fileSize) return '';
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (this.fileSize === 0) return '0 Byte';
        const i = parseInt(Math.floor(Math.log(this.fileSize) / Math.log(1024)));
        return Math.round(this.fileSize / Math.pow(1024, i), 2) + ' ' + sizes[i];
    }

    // Generate a human-readable summary of the report
    getSummary() {
        return {
            name: this.reportName,
            type: this.reportType,
            lastRun: this.lastRun ? new Date(this.lastRun).toLocaleString() : 'Never',
            status: this.status,
            scheduled: this.isScheduled() ? 'Yes' : 'No',
            fileSize: this.getFormattedSize(),
            reportDate: this.getFormattedDate(),
            fileName: this.fileName || 'No file',
            fileType: this.fileType || 'csv'
        };
    }
}

export class OSRReport extends Report {
    constructor(data) {
        super({
            ...data,
            reportType: 'osr'
        });
        this.gtfsProcessor = new GTFSProcessor();
    }

    static getDefaultParameters() {
        return {
            dateRange: { start: null, end: null },
            period: '1', // 1, 3, 6, or 12 months
            gtfsFile: null
        };
    }

    async generateReport() {
        if (!this.parameters.gtfsFile) {
            throw new Error('GTFS file is required for OSR report generation');
        }

        await this.gtfsProcessor.loadGTFSFile(this.parameters.gtfsFile);
        
        return this.gtfsProcessor.generateOSR({
            startDate: this.parameters.dateRange.start,
            endDate: this.parameters.dateRange.end,
            period: this.parameters.period
        });
    }
}